// src/components/sections/Requirements.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { authFetch } from "../../lib/auth";
import { isAppWebView } from "../../lib/openExternal";

export type ContractType =
  | "rent"
  | "goods"
  | "salary"
  | "maintenance"
  | "rental"
  | "drink";

type Props = {
  type: ContractType;
  className?: string;
  requireAdminReview?: boolean;
  hideWhenSatisfied?: boolean;
  onSatisfiedChange?: (ok: boolean) => void;
  sessionKey?: string;
};

type MyUpload = {
  id?: number | null;
  saved_name: string;
  original_name: string;
  name?: string | null;
  category: string | null;
  doc_type: string | null;
  doc_type_raw?: string | null;
  doc_type_label?: string | null;
  category_label?: string | null;
  status: string | null;
  created_at: string;
};

type DocDef = {
  key: string;
  title: string;
  docType: string;
  needCount?: number;
  hint?: string;
};

type StepRequirement = {
  docType: string;
  title: string;
  needCount: number;
  desc?: string;
};

type StepOption = {
  id: string;
  label: string;
  description?: string;
  requirements: StepRequirement[];
  completedLabel?: string;
};

type RequirementStep = {
  id: string;
  title: string;
  subtitle?: string;
  options: StepOption[];
};

const DRINK_DOCS: DocDef[] = [
  { key: "quote", title: "견적서", hint: "1건 이상", docType: "quote", needCount: 1 },
  {
    key: "oppbiz",
    title: "본인 사업자등록증",
    hint: "1건 이상",
    docType: "counterparty_biz_reg",
    needCount: 1,
  },
  {
    key: "oppbk",
    title: "상대방 통장사본",
    hint: "1건 이상 (또는 이체내역 2건 이상으로 대체 가능)",
    docType: "counterparty_bank_copy",
    needCount: 1,
  },
  {
    key: "opphist",
    title: "기존 이체내역",
    hint: "2건 이상 (통장사본이 없으면 이걸로 대체 가능)",
    docType: "counterparty_transfer_history",
    needCount: 2,
  },
];

const GOODS_DOCS: DocDef[] = [
  { key: "tax", title: "전자세금계산서", hint: "PDF 권장", docType: "tax_invoice", needCount: 1 },
  {
    key: "est",
    title: "견적서/거래명세서",
    hint: "서명/직인 포함",
    docType: "estimate_or_statement",
    needCount: 1,
  },
  { key: "mybiz", title: "본인 사업자등록증", docType: "my_biz_reg", needCount: 1 },
  { key: "oppbiz", title: "본인 사업자등록증", docType: "counterparty_biz_reg", needCount: 1 },
  { key: "oppbk", title: "상대방 통장사본", docType: "counterparty_bank_copy", needCount: 1 },
];

const SALARY_DOCS_REQUIRED: DocDef[] = [
  { key: "biz", title: "사업자등록증", docType: "biz_reg", needCount: 1 },
  { key: "pay", title: "급여명세서", docType: "payroll_statement", needCount: 1 },
  {
    key: "hst",
    title: "최근 급여 이체내역",
    docType: "salary_transfer_history",
    needCount: 2,
    hint: "2건 이상 제출",
  },
];
const SALARY_DOCS_OPTION_A: DocDef[] = [
  { key: "ins", title: "사대보험가입자명부", docType: "social_insurance_list", needCount: 1 },
];
const SALARY_DOCS_OPTION_B: DocDef[] = [
  { key: "wh", title: "원천징수영수증", docType: "withholding_receipt", needCount: 1 },
];

const MAINTENANCE_DOCS: DocDef[] = [
  { key: "mgm_maintenance", title: "관리비 영수증", docType: "mgmt_fee_maintenance", needCount: 2 },
];

const RENTAL_DOCS: DocDef[] = [
  { key: "rc", title: "렌탈계약서", docType: "rental_contract", needCount: 1 },
  { key: "rp", title: "렌탈료 납부내역", docType: "rental_payment_history", needCount: 2 },
];

const RENT_DOCS: DocDef[] = [
  { key: "rentc", title: "임대차계약서", docType: "rent_contract", needCount: 1 },
  { key: "mgm_rent", title: "관리비 영수증", docType: "mgmt_fee_rent", needCount: 1 },
  { key: "hist", title: "기존 임차료 이체내역", docType: "rent_history", needCount: 2 },
];

const ALL_DEFAULT_DOCS: DocDef[] = [
  ...DRINK_DOCS,
  ...GOODS_DOCS,
  ...SALARY_DOCS_REQUIRED,
  ...SALARY_DOCS_OPTION_A,
  ...SALARY_DOCS_OPTION_B,
  ...MAINTENANCE_DOCS,
  ...RENTAL_DOCS,
  ...RENT_DOCS,
];

const DEFAULT_DOC_MAP: Record<string, DocDef> = Object.fromEntries(
  ALL_DEFAULT_DOCS.map((doc) => [doc.docType, doc])
);

function getStaticDocsFor(type: ContractType): DocDef[] {
  switch (type) {
    case "drink":
      return DRINK_DOCS;
    case "rent":
      return RENT_DOCS;
    case "goods":
      return GOODS_DOCS;
    case "salary":
      return [...SALARY_DOCS_REQUIRED, ...SALARY_DOCS_OPTION_A, ...SALARY_DOCS_OPTION_B];
    case "maintenance":
      return MAINTENANCE_DOCS;
    case "rental":
      return RENTAL_DOCS;
    default:
      return [];
  }
}

function renderGuideText(text: string) {
  const lines = text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.replace(/^\s*[•·\-\–\—]\s*/, "").trim())
    .filter(Boolean);

  if (lines.length === 0) return null;
  if (lines.length === 1) return <p className="text-sm leading-7 text-slate-700">{lines[0]}</p>;

  return (
    <ul className="list-disc space-y-1.5 pl-5 text-sm leading-7 text-slate-700">
      {lines.map((ln, idx) => (
        <li key={`${idx}-${ln}`}>{ln}</li>
      ))}
    </ul>
  );
}

const DOC_TYPE_LABEL_FALLBACK: Record<string, string> = {
  quote: "견적서",
  counterparty_biz_reg: "본인 사업자등록증",
  counterparty_bank_copy: "상대방 통장사본",
  counterparty_transfer_history: "기존 이체내역",
  rent_contract: "임대차계약서",
  mgmt_fee_rent: "관리비 영수증",
  rent_history: "기존 임차료 이체내역",
  mgmt_fee_maintenance: "관리비 영수증",
  payroll_statement: "급여 증빙",
  salary_transfer_history: "이체내역",
  estimate_or_statement: "물품대금 증빙",
  rental_contract: "렌탈 증빙",
  rental_payment_history: "이체내역",
  social_insurance_list: "사대보험가입자명부",
  withholding_receipt: "원천징수영수증",
  my_biz_reg: "본인 사업자등록증",
  biz_reg: "사업자등록증",
  tax_invoice: "전자세금계산서",
  etc: "추가 서류",
};

function cleanText(v: unknown) {
  return String(v ?? "")
    .replace(/\r?\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeUploadsPayload(j: any): MyUpload[] {
  const items = Array.isArray(j?.uploads) ? j.uploads : Array.isArray(j?.items) ? j.items : Array.isArray(j) ? j : [];
  return items
    .map((x: any) => ({
      id: Number.isFinite(Number(x?.id)) ? Number(x.id) : null,
      saved_name: cleanText(x?.saved_name ?? x?.savedName ?? x?.filename ?? ""),
      original_name: cleanText(x?.original_name ?? x?.originalName ?? x?.name ?? ""),
      name: x?.name ?? null,
      category: x?.category ?? null,
      doc_type: x?.doc_type ?? x?.docType ?? null,
      doc_type_raw: x?.doc_type_raw ?? x?.docTypeRaw ?? null,
      doc_type_label: x?.doc_type_label ?? x?.docTypeLabel ?? null,
      category_label: x?.category_label ?? x?.categoryLabel ?? null,
      status: x?.status ?? null,
      created_at: x?.created_at ?? x?.createdAt ?? "",
    }))
    .map((u: MyUpload) => {
      const raw = cleanText(u.doc_type_raw || u.doc_type || "");
      const fromServer = cleanText(u.doc_type_label);
      const fallback = raw ? DOC_TYPE_LABEL_FALLBACK[raw] : "";
      const label =
        (fromServer && fromServer !== raw ? fromServer : "") || fallback || cleanText(u.doc_type) || "";
      return { ...u, doc_type_label: label };
    })
    .filter((x: MyUpload) => !!(x.saved_name || x.original_name || x.id));
}

function docTypeKey(u: MyUpload) {
  return cleanText(u.doc_type_raw || u.doc_type || "");
}

function docTypeLabel(u: MyUpload) {
  return cleanText(u.doc_type_label || "");
}

function formatNeedLabel(requirements: StepRequirement[]) {
  if (requirements.length === 1) return `${requirements[0].needCount}건`;
  const total = requirements.reduce((sum, req) => sum + req.needCount, 0);
  return `총 ${total}건`;
}

function getFallbackGuide(type: ContractType) {
  if (type === "drink") {
    return [
      "• 견적서 1건 이상 제출이 필요합니다.",
      "• 본인 사업자등록증 1건 제출이 필요합니다.",
      "• 상대방 통장사본 1건 또는 기존 거래 이체내역 2건 이상 중 하나를 제출해 주세요.",
    ].join("\n");
  }

  if (type === "rent") {
    return [
      "• 임대차계약서 1건 제출 시 완료로 인정됩니다. (필수)",
      "• 계약서가 없을 경우: 관리비 영수증 1건 + 기존 임차료 이체내역 2건 이상을 모두 제출해야 합니다. (대체 가능)",
    ].join("\n");
  }

  if (type === "goods") {
    return [
      "• 전자세금계산서 1건 (필수)",
      "• 견적서 또는 거래명세서 1건 (필수)",
      "• 본인 사업자등록증 1건 (필수)",
      "• 본인 사업자등록증 1건 (필수)",
      "• 상대방 통장사본 1건 (필수)",
    ].join("\n");
  }

  if (type === "salary") {
    return [
      "• 사업자등록증 1건 (필수)",
      "• 급여명세서 1건 (필수)",
      "• 최근 급여 이체내역 2건 이상 (필수)",
      "• 사대보험가입자명부 1건 또는 원천징수영수증 1건 (둘 중 하나)",
    ].join("\n");
  }

  if (type === "maintenance") return "• 관리비 영수증 2건 이상 (필수)";
  if (type === "rental") return ["• 렌탈계약서 1건 (필수)", "• 렌탈료 납부내역 2건 이상 (필수)"].join("\n");
  return "";
}

function buildRequirementSteps(type: ContractType, docs: DocDef[]) {
  const getDoc = (docType: string, fallbackTitle: string, fallbackNeed = 1, fallbackDesc = ""): StepRequirement => {
    const fromActive = docs.find((doc) => doc.docType === docType);
    const fromDefault = DEFAULT_DOC_MAP[docType];
    const title = fromActive?.title || fromDefault?.title || fallbackTitle;
    const needCount = fromActive?.needCount || fromDefault?.needCount || fallbackNeed;
    const desc = fromActive?.hint || fromDefault?.hint || fallbackDesc;
    return { docType, title, needCount, desc: desc || undefined };
  };

  switch (type) {
    case "drink":
      return [
        {
          id: "quote",
          title: "견적서",
          options: [{ id: "quote", label: "견적서", completedLabel: "견적서 완료", requirements: [getDoc("quote", "견적서", 1)] }],
        },
        {
          id: "oppbiz",
          title: "본인 사업자등록증",
          options: [
            {
              id: "oppbiz",
              label: "사업자등록증",
              completedLabel: "본인 사업자등록증 완료",
              requirements: [getDoc("counterparty_biz_reg", "본인 사업자등록증", 1)],
            },
          ],
        },
        {
          id: "account-proof",
          title: "입금 계좌 증빙",
          subtitle: "상대방 통장사본 또는 기존 거래 이체내역 중 하나를 선택해 주세요.",
          options: [
            {
              id: "bank-copy",
              label: "상대방 통장사본",
              completedLabel: "상대방 통장사본 완료",
              requirements: [getDoc("counterparty_bank_copy", "상대방 통장사본", 1)],
            },
            {
              id: "transfer-history",
              label: "기존 거래 이체내역",
              description: "통장사본이 없으면 기존 거래 이체내역 2건 이상으로 대체할 수 있습니다.",
              completedLabel: "기존 거래 이체내역 완료",
              requirements: [getDoc("counterparty_transfer_history", "기존 거래 이체내역", 2)],
            },
          ],
        },
      ] satisfies RequirementStep[];
    case "goods":
      return [
        {
          id: "tax",
          title: "전자세금계산서",
          options: [
            {
              id: "tax",
              label: "전자세금계산서",
              completedLabel: "전자세금계산서 완료",
              requirements: [getDoc("tax_invoice", "전자세금계산서", 1, "PDF 권장")],
            },
          ],
        },
        {
          id: "goods-proof",
          title: "물품대금 증빙",
          subtitle: "견적서 또는 거래명세서 중 하나를 선택해 업로드해 주세요.",
          options: [
            {
              id: "estimate",
              label: "견적서",
              completedLabel: "견적서 완료",
              requirements: [getDoc("estimate_or_statement", "견적서/거래명세서", 1, "서명/직인 포함")],
            },
            {
              id: "statement",
              label: "거래명세서",
              completedLabel: "거래명세서 완료",
              requirements: [getDoc("estimate_or_statement", "견적서/거래명세서", 1, "서명/직인 포함")],
            },
          ],
        },
        {
          id: "mybiz",
          title: "본인 사업자등록증",
          options: [{ id: "mybiz", label: "본인 사업자등록증", completedLabel: "본인 사업자등록증 완료", requirements: [getDoc("my_biz_reg", "본인 사업자등록증", 1)] }],
        },
        {
          id: "oppbiz",
          title: "본인 사업자등록증",
          options: [{ id: "oppbiz", label: "본인 사업자등록증", completedLabel: "본인 사업자등록증 완료", requirements: [getDoc("counterparty_biz_reg", "본인 사업자등록증", 1)] }],
        },
        {
          id: "oppbank",
          title: "상대방 통장사본",
          options: [{ id: "oppbank", label: "상대방 통장사본", completedLabel: "상대방 통장사본 완료", requirements: [getDoc("counterparty_bank_copy", "상대방 통장사본", 1)] }],
        },
      ] satisfies RequirementStep[];
    case "salary":
      return [
        {
          id: "biz",
          title: "사업자등록증",
          options: [{ id: "biz", label: "사업자등록증", completedLabel: "사업자등록증 완료", requirements: [getDoc("biz_reg", "사업자등록증", 1)] }],
        },
        {
          id: "payroll",
          title: "급여명세서",
          options: [{ id: "payroll", label: "급여명세서", completedLabel: "급여명세서 완료", requirements: [getDoc("payroll_statement", "급여명세서", 1)] }],
        },
        {
          id: "salary-history",
          title: "최근 급여 이체내역",
          options: [{ id: "salary-history", label: "이체내역", completedLabel: "최근 급여 이체내역 완료", requirements: [getDoc("salary_transfer_history", "최근 급여 이체내역", 2)] }],
        },
        {
          id: "salary-option",
          title: "추가 급여 증빙",
          subtitle: "사대보험가입자명부 또는 원천징수영수증 중 하나를 선택해 주세요.",
          options: [
            {
              id: "insurance",
              label: "사대보험가입자명부",
              completedLabel: "사대보험가입자명부 완료",
              requirements: [getDoc("social_insurance_list", "사대보험가입자명부", 1)],
            },
            {
              id: "withholding",
              label: "원천징수영수증",
              completedLabel: "원천징수영수증 완료",
              requirements: [getDoc("withholding_receipt", "원천징수영수증", 1)],
            },
          ],
        },
      ] satisfies RequirementStep[];
    case "maintenance":
      return [
        {
          id: "maintenance",
          title: "관리비 영수증",
          options: [{ id: "maintenance", label: "관리비 영수증", completedLabel: "관리비 영수증 완료", requirements: [getDoc("mgmt_fee_maintenance", "관리비 영수증", 2)] }],
        },
      ] satisfies RequirementStep[];
    case "rental":
      return [
        {
          id: "rental-contract",
          title: "렌탈계약서",
          options: [{ id: "rental-contract", label: "렌탈계약서", completedLabel: "렌탈계약서 완료", requirements: [getDoc("rental_contract", "렌탈계약서", 1)] }],
        },
        {
          id: "rental-payment",
          title: "렌탈료 납부내역",
          options: [{ id: "rental-payment", label: "렌탈료 납부내역", completedLabel: "렌탈료 납부내역 완료", requirements: [getDoc("rental_payment_history", "렌탈료 납부내역", 2)] }],
        },
      ] satisfies RequirementStep[];
    case "rent":
      return [
        {
          id: "rent-proof",
          title: "임대료 증빙",
          subtitle: "임대차계약서 1건 또는 대체서류 조합 중 하나를 선택해 주세요.",
          options: [
            {
              id: "rent-contract",
              label: "임대차계약서",
              completedLabel: "임대차계약서 완료",
              requirements: [getDoc("rent_contract", "임대차계약서", 1)],
            },
            {
              id: "rent-substitute",
              label: "대체 서류",
              description: "관리비 영수증 1건과 기존 임차료 이체내역 2건 이상을 제출합니다.",
              completedLabel: "대체 서류 완료",
              requirements: [
                getDoc("mgmt_fee_rent", "관리비 영수증", 1),
                getDoc("rent_history", "기존 임차료 이체내역", 2),
              ],
            },
          ],
        },
      ] satisfies RequirementStep[];
    default:
      return [];
  }
}

function isOptionMet(option: StepOption, counts: Record<string, number>) {
  return option.requirements.every((req) => (counts[req.docType] ?? 0) >= req.needCount);
}

function getCurrentRequirement(option: StepOption, counts: Record<string, number>) {
  return option.requirements.find((req) => (counts[req.docType] ?? 0) < req.needCount) ?? option.requirements[0] ?? null;
}

function formatCreatedAt(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("ko-KR");
}

export default function Requirements({
  type,
  className,
  requireAdminReview = false,
  hideWhenSatisfied = false,
  onSatisfiedChange,
  sessionKey = "default",
}: Props) {
  const [items, setItems] = useState<MyUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyDocType, setBusyDocType] = useState<string | null>(null);
  const [docs, setDocs] = useState<DocDef[]>(() => getStaticDocsFor(type));
  const [guideText, setGuideText] = useState<string>("");
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});

  const loadUploads = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`/api/uploads/my?session_key=${encodeURIComponent(sessionKey)}`);
      const j = await r.json().catch(() => null);
      setItems(normalizeUploadsPayload(j));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUploads();
  }, [sessionKey]);

  useEffect(() => {
    let cancelled = false;

    async function loadDocsAndGuide() {
      const fallback = getStaticDocsFor(type);
      if (!cancelled) {
        setDocs(fallback);
        setGuideText("");
        setSelectedOptions({});
      }

      if (type === "drink") return;

      try {
        const res = await authFetch(`/api/public/category-docs?category=${encodeURIComponent(type)}`);
        const j = await res.json().catch(() => null);
        const row0 = Array.isArray(j?.items) ? j.items[0] : null;
        const candidateGuide =
          row0?.body ??
          j?.item?.body ??
          j?.body ??
          row0?.guide_text ??
          row0?.guideText ??
          j?.guide_text ??
          j?.guideText ??
          row0?.notice_text ??
          j?.notice_text ??
          "";

        if (!cancelled) setGuideText(String(candidateGuide || "").trim());

        const raw = j?.docs || j?.list || j?.data || j?.rows || [];
        if (!Array.isArray(raw) || raw.length === 0) return;

        const mapped: DocDef[] = raw
          .map((row: any) => {
            const docType = String(row.doc_type ?? row.docType ?? "").trim();
            const title = String(row.title ?? "").trim();
            if (!docType || !title) return null;
            const key = String(row.doc_key ?? row.key ?? docType).trim();
            const needRaw = row.need_count ?? row.needCount ?? undefined;
            const needCount = typeof needRaw === "number" && needRaw > 0 ? needRaw : undefined;
            const hintText = String(row.hint ?? row.description ?? row.note ?? "").trim();
            return { key, title, docType, needCount, hint: hintText || undefined } as DocDef;
          })
          .filter((d): d is DocDef => !!d);

        if (!cancelled && mapped.length) setDocs(mapped);
      } catch {
        /* fallback 유지 */
      }
    }

    void loadDocsAndGuide();
    return () => {
      cancelled = true;
    };
  }, [type]);

  const allow = (u: MyUpload) => !requireAdminReview || u.status === "approved";

  const counts = useMemo(() => {
    const result: Record<string, number> = {};
    for (const item of items) {
      const docType = docTypeKey(item);
      if (!allow(item) || !docType) continue;
      result[docType] = (result[docType] ?? 0) + 1;
    }
    return result;
  }, [items, requireAdminReview]);

  const docUploads = useMemo(() => {
    const result: Record<string, MyUpload[]> = {};
    for (const item of items) {
      const docType = docTypeKey(item);
      if (!allow(item) || !docType) continue;
      if (!result[docType]) result[docType] = [];
      result[docType].push(item);
    }
    return result;
  }, [items, requireAdminReview]);

  const activeDocs = useMemo(() => (docs.length ? docs : getStaticDocsFor(type)), [docs, type]);
  const steps = useMemo(() => buildRequirementSteps(type, activeDocs), [type, activeDocs]);

  const stepStates = useMemo(() => {
    return steps.map((step) => {
      const satisfiedOption = step.options.find((option) => isOptionMet(option, counts)) || null;
      const selectedOptionId = selectedOptions[step.id] || satisfiedOption?.id || step.options[0]?.id || "";
      const selectedOption = step.options.find((option) => option.id === selectedOptionId) || step.options[0] || null;
      return {
        step,
        satisfiedOption,
        selectedOptionId,
        selectedOption,
        complete: !!satisfiedOption,
      };
    });
  }, [counts, selectedOptions, steps]);

  const satisfied = stepStates.every((state) => state.complete);
  const currentState = stepStates.find((state) => !state.complete) || null;
  const currentOption = currentState?.selectedOption || null;
  const currentRequirement = currentOption ? getCurrentRequirement(currentOption, counts) : null;
  const currentFiles = currentRequirement ? docUploads[currentRequirement.docType] || [] : [];

  useEffect(() => {
    onSatisfiedChange?.(satisfied);
  }, [onSatisfiedChange, satisfied]);

  const fallbackGuide = useMemo(() => getFallbackGuide(type), [type]);
  const guideNode = useMemo(() => {
    return (
      <ol className="space-y-2 text-[14px] leading-[1.55] text-slate-700 sm:space-y-3 sm:text-[18px]">
        <li>견적서 1건 이상</li>
        <li>본인 사업자등록증 1건</li>
        <li>상대방 통장사본 또는 기존 거래 이체내역 2건</li>
      </ol>
    );
  }, []);

  const completedLabels = useMemo(() => {
    return stepStates
      .filter((state) => state.complete && state.satisfiedOption)
      .map((state) => state.satisfiedOption?.completedLabel || `${state.step.title} 완료`)
      .filter(Boolean) as string[];
  }, [stepStates]);

  const allFiles = useMemo(() => {
    return [...items].filter(allow).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  }, [items, requireAdminReview]);

  async function uploadFiles(docType: string, files: FileList | null, category: ContractType) {
    if (!files || files.length === 0) return false;
    setBusyDocType(docType);
    try {
      const fd = new FormData();
      Array.from(files).forEach((file) => fd.append("files", file));
      fd.append("doc_type", docType);
      fd.append("category", category);
      fd.append("session_key", sessionKey);

      const resp = await authFetch("/api/uploads", { method: "POST", body: fd });
      const j = await resp.json().catch(() => null);
      if (!resp.ok || !j?.ok) throw new Error(j?.message || "업로드 실패");
      await loadUploads();
      alert("업로드 완료되었습니다.");
      return true;
    } catch (e: any) {
      alert(e?.message || "업로드 중 오류가 발생했습니다.");
      return false;
    } finally {
      setBusyDocType(null);
    }
  }

  async function previewFile(u: MyUpload) {
    try {
      let resp: Response | null = null;
      if (u.id) resp = await authFetch(`/api/uploads/preview-id/${u.id}`);
      if (!resp || !resp.ok) {
        const key = u.saved_name || u.original_name || u.name || "";
        if (!key) throw new Error("미리보기할 파일 키가 없습니다.");
        resp = await authFetch(`/api/uploads/preview/${encodeURIComponent(key)}?debug=1`);
      }
      if (!resp.ok) {
        const j = await resp.json().catch(() => null);
        throw new Error(j?.message || "미리보기에 실패했습니다.");
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      alert(e?.message || "미리보기 중 오류가 발생했습니다.");
    }
  }

  async function deleteFile(u: MyUpload) {
    if (!window.confirm("이 파일을 삭제하시겠습니까?")) return;
    setBusyDocType(u.doc_type || null);
    try {
      const key = u.id ? String(u.id) : u.saved_name || "";
      if (!key) throw new Error("삭제할 파일 키가 없습니다.");
      const resp = await authFetch(`/api/uploads/${encodeURIComponent(key)}`, { method: "DELETE" });
      const j = await resp.json().catch(() => null);
      if (!resp.ok || !j?.ok) throw new Error(j?.message || "삭제 실패");
      await loadUploads();
    } catch (e: any) {
      alert(e?.message || "삭제 중 오류가 발생했습니다.");
    } finally {
      setBusyDocType(null);
    }
  }

  if (hideWhenSatisfied && satisfied) return null;

  return (
    <section className={className}>
      <div className="rounded-[20px] bg-white p-1.5 sm:rounded-[24px] sm:p-3">
        <div className="rounded-[20px] border border-slate-200 bg-white px-4 py-4 sm:rounded-[22px] sm:px-6 sm:py-6">
          <div className="mb-3 text-[17px] font-extrabold text-slate-800 sm:mb-4 sm:text-[20px]">서류 안내</div>
          <div>{guideNode ?? <span className="text-sm text-slate-500">안내문이 없습니다.</span>}</div>
          <div className="mt-8 border-t border-slate-100 pt-3 text-[12px] font-medium text-slate-500 sm:mt-20 sm:pt-4 sm:text-[15px]">
            ※ 필요시 추가 서류를 요청 할 수 있습니다.
          </div>
        </div>

        {false && completedLabels.length > 0 && (
          <div className="mt-4 rounded-[18px] bg-white p-3">
            <div className="mb-2 text-xs font-extrabold text-slate-700">업로드 완료된 서류</div>
            <div className="flex flex-wrap gap-2">
              {completedLabels.map((label) => (
                <span key={label} className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}

        {currentState && currentOption && currentRequirement ? (
          <div className="mt-4 sm:mt-5">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {stepStates.map((state) => {
                const active = currentState?.step.id === state.step.id;
                const option = state.satisfiedOption || state.selectedOption;
                const label = option?.completedLabel?.replace(/\s*완료$/, "") || option?.label || state.step.title;
                return (
                  <button
                    key={state.step.id}
                    type="button"
                    onClick={() => {
                      if (!state.complete) {
                        setSelectedOptions((prev) => ({ ...prev, [state.step.id]: state.selectedOptionId }));
                      }
                    }}
                    className={[
                      "min-h-[48px] rounded-[16px] border px-2.5 py-2 text-center text-[12px] font-extrabold transition sm:min-h-[66px] sm:rounded-[18px] sm:px-3 sm:py-3 sm:text-[15px]",
                      active
                        ? "border-[#f97316] bg-[#f97316] text-white shadow-[0_14px_28px_rgba(249,115,22,0.22)]"
                        : state.complete
                          ? "border-[#fed7aa] bg-[#fff7ed] text-[#c2410c]"
                          : "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {currentState.step.options.length > 1 ? (
              <div className="mt-3 grid grid-cols-2 gap-2 rounded-[18px] bg-[#fff7ed] p-1.5 sm:mt-4">
                {currentState.step.options.map((option) => {
                  const selected = option.id === currentOption.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setSelectedOptions((prev) => ({ ...prev, [currentState.step.id]: option.id }))}
                      className={[
                        "rounded-[16px] border px-3 py-2 text-[12px] font-extrabold transition sm:py-2.5 sm:text-[15px]",
                        selected ? "border-[#f97316] bg-[#f97316] text-white shadow-sm" : "border-transparent bg-white text-slate-700",
                      ].join(" ")}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            ) : null}

            <div className="mt-5 rounded-[16px] bg-white px-2 sm:mt-8 sm:px-3">
              {currentFiles.length > 0 ? (
                <div className="mb-3 rounded-[14px] border border-slate-200 bg-slate-50 p-2.5 sm:p-3">
                  <ul className="space-y-2">
                    {currentFiles.map((file) => (
                      <li
                        key={`${file.id ?? "x"}-${file.saved_name}-${file.original_name}`}
                        className="rounded-[12px] border border-slate-200 bg-white px-3 py-2.5 sm:px-4 sm:py-3"
                      >
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-[11px] font-black text-white">
                            ✓
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold text-slate-800 sm:text-base">
                              {file.original_name || file.name || file.saved_name}
                            </div>
                            <div className="mt-1 flex items-center justify-between gap-3 text-xs text-slate-500">
                              <span>{formatCreatedAt(file.created_at) || docTypeLabel(file) || currentRequirement.title}</span>
                              <div className="flex items-center gap-2">
                                <button type="button" className="font-semibold text-slate-700" onClick={() => previewFile(file)}>
                                  미리보기
                                </button>
                                <button type="button" className="font-semibold text-rose-500" onClick={() => deleteFile(file)}>
                                  삭제
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <SequentialUploadButton
                busy={busyDocType === currentRequirement.docType}
                docType={currentRequirement.docType}
                category={type}
                onUpload={uploadFiles}
              />
            </div>

            {false && currentOption.description ? <p className="mt-3 text-sm text-slate-600">{currentOption.description}</p> : null}
          </div>
        ) : !loading ? (
          <div className="mt-4 rounded-[14px] bg-white p-4 text-sm font-bold text-slate-700">
            모든 필수 서류 업로드가 완료되었습니다.
          </div>
        ) : null}

        {false && allFiles.length > 0 ? (
          <div className="mt-4 rounded-[14px] bg-white p-4">
            <div className="mb-3 text-xs font-extrabold text-slate-500">전체 업로드 파일</div>
            <div className="space-y-2">
              {allFiles.slice(0, 5).map((file) => (
                <div
                  key={`all-${file.id ?? "x"}-${file.saved_name}-${file.original_name}`}
                className="flex items-center justify-between gap-3 rounded-[12px] bg-white px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-800">{file.original_name || file.name || file.saved_name}</div>
                    <div className="mt-1 text-xs text-slate-500">{docTypeLabel(file) || "추가 서류"}</div>
                  </div>
                  <button type="button" className="text-xs font-bold text-slate-700" onClick={() => previewFile(file)}>
                    보기
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-1 text-right">
          {loading ? <span className="text-xs text-slate-500">불러오는 중…</span> : null}
        </div>
      </div>

    </section>
  );
}

function SequentialUploadButton(props: {
  busy?: boolean;
  docType: string;
  category: ContractType;
  onUpload: (docType: string, files: FileList | null, category: ContractType) => Promise<boolean>;
  onDone?: () => void;
}) {
  const { busy, docType, category, onUpload, onDone } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const openPicker = () => {
    if (busy) return;
    const input = inputRef.current;
    if (!input) return;
    if (isAppWebView()) {
      // 앱에서는 시스템 선택창(카메라/파일)을 바로 띄움
      input.accept = "image/*";
      input.multiple = false;
      input.removeAttribute("capture");
    } else {
      input.accept = ".pdf,image/*";
      input.multiple = true;
    }
    input.click();
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,image/*"
        multiple
        className="hidden"
        onChange={async (e) => {
          const files = e.currentTarget.files;
          const ok = await onUpload(docType, files, category);
          e.currentTarget.value = "";
          e.currentTarget.accept = ".pdf,image/*";
          e.currentTarget.multiple = true;
          e.currentTarget.removeAttribute("capture");
          if (ok) onDone?.();
        }}
      />

      <button
        type="button"
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-[16px] bg-[#f97316] px-4 py-2.5 text-sm font-extrabold text-white transition hover:bg-[#ea580c] active:translate-y-px disabled:opacity-60 sm:mt-4 sm:rounded-[18px] sm:py-3 sm:text-base"
        onClick={openPicker}
        disabled={busy}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor">
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M21 12.5V7a5 5 0 0 0-10 0v9a3 3 0 0 0 6 0V8.5" />
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M11 12v4a5 5 0 0 1-10 0v-5a7 7 0 1 1 14 0v6" />
        </svg>
        {busy ? "처리 중…" : "파일 업로드"}
      </button>
    </>
  );
}
